// assetFileDownload — fetch an asset's file and hand it to the browser as a
// download. The I/O shell; the decisions live in assetDownloadRules.js.
//
// It exists so the content browser has ONE download path instead of a copy per
// call site (RigInspector grew its own; blueprintAssetTransfer has its own for
// a generated file, which is a different job — it has no server object to GET).
//
// Two rules it enforces:
//   - the fetch goes through fetchAamFile for an asset-manager-relative url, so
//     the AAM base and the auth header are applied — the reason is in
//     assetDownloadRules' header, and getting it wrong reads as a 404 from a
//     server that has the file;
//   - a failure THROWS with the status in the message. A download that quietly
//     writes nothing is indistinguishable from one the browser is still doing.

import { fetchAamFile } from './assetManagerApi'
// IMPORTED, then re-exported — and it has to be both. `export { saveBlob } from
// './saveBlob.js'` alone is a RE-EXPORT: it forwards the name to this module's
// consumers and creates NO local binding, so `downloadAssetFile`'s own call
// below became a ReferenceError. Nothing static catches that — it is valid JS,
// the bundler emits it, `tsc` over a JS file does not resolve it — and the
// symptom is a download that silently never starts. Caught by
// `tests/e2e/ci/asset-download.spec.js` waiting 15 s for a `download` event.
import { saveBlob } from './saveBlob.js'
// The four lines are a leaf so nobody has to import the AAM client to save a
// file; this keeps the name reachable from here for callers that already do.
export { saveBlob }
import { assetDownloadTarget, downloadUnavailableReason } from './assetDownloadRules'

/**
 * Download one asset's file. Resolves `{ filename, bytes }`.
 *
 * `onProgress({ received, total })` fires as the body streams — `total` is null
 * when the server sent no Content-Length (a compressed body at the edge), which
 * is why the caller must be able to render a byte count rather than a percent.
 */
export async function downloadAssetFile(asset, { onProgress } = {}) {
  const target = assetDownloadTarget(asset)
  if (!target) throw new Error(downloadUnavailableReason(asset))

  const res = target.viaAam
    ? await fetchAamFile(target.url)
    : await fetch(target.url)
  if (!res.ok) {
    throw new Error(`Download failed: ${res.status} ${res.statusText || ''}`.trim())
  }

  const blob = await readBody(res, onProgress)
  saveBlob(blob, target.filename)
  return { filename: target.filename, bytes: blob.size }
}

// Stream the body so a 70 MB decoder reports progress instead of sitting silent
// (Doherty: a wait with no visible progress reads as a hang). A body with no
// reader — a mocked response in a test, an old browser — falls back to .blob().
async function readBody(res, onProgress) {
  const header = res.headers?.get?.('content-length')
  const total = header != null && header !== '' && Number.isFinite(Number(header))
    ? Number(header)
    : null

  const reader = res.body?.getReader?.()
  if (!reader) {
    const blob = await res.blob()
    onProgress?.({ received: blob.size, total: total ?? blob.size })
    return blob
  }

  const chunks = []
  let received = 0
  onProgress?.({ received: 0, total })
  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    if (value) {
      chunks.push(value)
      received += value.byteLength ?? value.length ?? 0
      onProgress?.({ received, total })
    }
  }
  return new Blob(chunks, { type: res.headers?.get?.('content-type') || 'application/octet-stream' })
}
